<template>
  <div class="editor-container">
    <div class="editor-header">
      <span class="file-type" />
      <div class="header-buttons">
        <el-button v-if="!isRevision" type="text" icon="el-icon-edit" @click="toggleEditMode">{{ isEditMode ? '只读' : '编辑' }}</el-button>
        <el-button type="text" icon="el-icon-document-copy" @click="copyCode">Copy</el-button>
      </div>
    </div>
    <div ref="editorWrapper" class="editor-wrapper">
      <pre v-show="!isEditMode" class="code-display" v-html="highlightedCode" />
      <textarea
        v-show="isEditMode"
        ref="editor"
        v-model="codeExample"
        class="code-editor"
        spellcheck="false"
        @input="updateHighlight"
        @scroll="syncScroll"
      />
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import hljs from 'highlight.js'
import 'highlight.js/styles/vs2015.css'
import beautify from 'js-beautify'
import ElementUI from 'element-ui'
import 'element-ui/lib/theme-chalk/index.css'
import AiCustomPageDataView from './aiCustomPageDataView.vue'
import store from '@/store'
import { functionRegistry } from '@/store/modules/aiCustomPage/function-registry'

// 创建虚拟Vue实例
const VirtualVue = Vue.extend({
  components: {
    AiCustomPageDataView
  },
  store,
  beforeCreate() {
    // 为虚拟实例创建本地console
    this.$console = {
      log: () => {},
      warn: () => {},
      error: () => {}
    }
  }
})
VirtualVue.use(ElementUI)

export default {
  name: 'AiCustomPageCodeView',
  props: {
    language: {
      type: String,
      default: 'html'
    },
    // 页面部件数组，用于生成代码
    pageParts: {
      type: Array,
      required: true
    },
    // 表单配置
    formConfig: {
      type: Object,
      required: true
    },
    // 表单数据
    formData: {
      type: Object,
      required: true
    },
    // 控制是否显示代码视图
    visible: {
      type: Boolean,
      default: false
    },
    // 是否为历史版本
    isRevision: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      codeExample: '',
      codeExampleBackup: '',
      isEditMode: false,
      highlightedCode: '',
      lastHighlightTime: 0
    }
  },
  computed: {
    formUuid() {
      // 这个有用，请勿修改
      const val = this.formConfig?.uuid
      return val != null ? val : null
    }
  },
  watch: {
    codeExample: {
      handler(newVal) {
        console.log('[代码页] 代码内容已更改')
        if (this.isEditMode) {
          this.updateHighlight()
        }
      },
      deep: true
    },
    isEditMode(newVal) {
      console.log('[代码页] 编辑模式已更改:', newVal)
    },
    visible: {
      handler(newVal) {
        if (newVal) {
          this.generateCode()
        }
      },
      immediate: true
    }
  },
  async mounted() {
    await this.generateCode()
  },
  methods: {
    // 构建函数块
    buildFunctionBlock(func) {
      // 构建注释行
      const commentLines = [
        '/*',
        `用户输入: ${func.metadata.sourceQuery}`,
        `规则ID: ${func.id}`,
        `规则类型: ${func.type === 'validation' ? '验证' : func.type === 'sync' ? '同步' : '可见性'}`,
        func.type === 'validation' ? `错误提示: ${func.metadata.errorMessage}` : '',
        `涉及字段: ${func.metadata.elements.join(', ')}`,
        func.type === 'sync' ? `触发字段: ${func.metadata.trigger.element}` : '',
        func.type === 'visibility' ? `目标字段: ${func.metadata.targetElement}` : '',
        '*/'
      ]

      // 代码段的json格式化容易引起解析转义问题，很麻烦
      const { code, ...funcCopy } = func
      funcCopy.code = '...'

      // 提取函数部分
      const functionMatch = code.match(/function\s+\w+\s*\([^)]*\)\s*\{[\s\S]*\}/)
      const functionCode = functionMatch ? functionMatch[0] : code

      // 构建函数块
      return [
        '<!--',
        JSON.stringify(funcCopy, null, 2),
        '-->',
        '<script>',
        commentLines.join('\n'),
        functionCode,
        '<\/script>'
      ].join('\n\n')
    },
    // 生成代码
    async generateCode() {
      const startTime = performance.now()
      console.log('[代码页] 开始生成代码，当前表单uuid:', this.formUuid)
      try {
        if (this.isRevision) {
          // 修订模式下直接使用存储的代码
          this.codeExample = this.formConfig.formCode
          this.updateHighlight()
          console.log('[代码页] 修订模式代码生成完成，耗时:', performance.now() - startTime, 'ms')
          return
        }

        // 创建虚拟Vue实例
        const vm = new VirtualVue({
          render: h => h(AiCustomPageDataView, {
            props: {
              pageParts: this.pageParts,
              formConfig: this.formConfig,
              formData: this.formData,
              debug: true,
              readonly: false,
              jobUuid: null
            }
          })
        }).$mount()

        // 获取HTML字符串
        const htmlCode = vm.$el.outerHTML

        // 获取并格式化函数
        const validationFunctions = functionRegistry.getValidationFunctions(this.formUuid)
        const syncFunctions = functionRegistry.getSyncFunctions(this.formUuid)
        const visibilityFunctions = functionRegistry.getVisibilityFunctions(this.formUuid)

        console.log('[代码页] 从函数注册表获取的函数数量:', {
          validation: validationFunctions.length,
          sync: syncFunctions.length,
          visibility: visibilityFunctions.length
        })

        // 格式化函数为注释和脚本
        const functionBlocks = []

        // 处理验证函数、同步函数和可见性函数
        for (const func of validationFunctions) {
          func.type = 'validation'
          functionBlocks.push(this.buildFunctionBlock(func) + '\n\n')
        }
        for (const func of syncFunctions) {
          func.type = 'sync'
          functionBlocks.push(this.buildFunctionBlock(func) + '\n\n')
        }
        for (const func of visibilityFunctions) {
          func.type = 'visibility'
          functionBlocks.push(this.buildFunctionBlock(func) + '\n\n')
        }

        // 将函数块添加到HTML代码前面
        const fullCode = functionBlocks.join('') + htmlCode

        // 美化完整代码
        this.codeExample = beautify.html(fullCode, {
          indent_size: 2,
          indent_char: ' ',
          max_preserve_newlines: 1,
          preserve_newlines: true,
          keep_array_indentation: false,
          break_chained_methods: false,
          indent_scripts: 'normal',
          brace_style: 'collapse',
          space_before_conditional: true,
          unescape_strings: false,
          jslint_happy: false,
          end_with_newline: true,
          wrap_line_length: 0,
          indent_inner_html: true,
          comma_first: false,
          e4x: false,
          indent_empty_lines: false,
          wrap_attributes: 'force',
          unformatted: ['pre', 'code'],
          content_unformatted: ['pre', 'code', 'script']
        })

        this.updateHighlight()

        // 更新formCode字段
        console.log('[代码页] 代码生成完成，准备发出update:formCode事件，耗时:', performance.now() - startTime, 'ms')
        this.$emit('update:formCode', this.codeExample)
        console.log('[代码页] update:formCode事件已发出，总耗时:', performance.now() - startTime, 'ms')
      } catch (error) {
        console.error('[代码页] 生成代码时出错:', error)
        this.$message.error('生成代码时出错')
      }
    },
    // 解析和验证代码
    parseAndValidateCode(code) {
      console.log('[代码页] 开始解析和验证代码...')
      // 匹配规则块的正则表达式
      // 格式: <!-- {json} --> <script> ... <\/script>
      const ruleBlockRegex = /<!--\s*({[\s\S]*?})\s*-->\s*<script>([\s\S]*?)<\/script>/g

      const rules = []
      let isValid = true
      let match

      // 遍历所有规则块
      while ((match = ruleBlockRegex.exec(code)) !== null) {
        try {
          const [, jsonStr, funcCode] = match
          console.log('[代码页] 解析规则块:', { jsonStr: jsonStr.slice(0, 100) + '...', funcCode: funcCode.slice(0, 100) + '...' })

          // 解析JSON
          const ruleData = JSON.parse(jsonStr)

          // 验证规则结构
          if (!this.validateRuleStructure(ruleData)) {
            console.error('[代码页] 规则结构无效:', ruleData)
            isValid = false
            break
          }

          // 验证函数代码
          if (!this.validateFunctionCode(funcCode)) {
            console.error('[代码页] 函数代码无效:', funcCode)
            isValid = false
            break
          }

          rules.push(ruleData)
        } catch (error) {
          console.error('[代码页] 解析规则块失败:', error)
          isValid = false
          break
        }
      }

      console.log(`[代码页] 代码验证完成，共找到 ${rules.length} 个规则，验证结果: ${isValid}`)
      return { rules, isValid }
    },

    // 验证规则结构
    validateRuleStructure(rule) {
      // 验证规则JSON结构
      const requiredFields = ['id', 'type', 'metadata']
      const requiredMetadataFields = ['sourceQuery', 'elements']

      // 检查基本字段
      if (!requiredFields.every(field => rule[field] !== undefined)) {
        console.error('[代码页] 缺少必要字段:', requiredFields.filter(field => !rule[field]))
        return false
      }

      // 检查metadata字段
      if (!requiredMetadataFields.every(field => rule.metadata[field] !== undefined)) {
        console.error('[代码页] 缺少必要metadata字段:', requiredMetadataFields.filter(field => !rule.metadata[field]))
        return false
      }

      // 验证类型
      if (!['validation', 'sync', 'visibility'].includes(rule.type)) {
        console.error('[代码页] 无效的规则类型:', rule.type)
        return false
      }

      // 验证sync类型特有的字段
      if (rule.type === 'sync' && !rule.metadata.trigger?.element) {
        console.error('[代码页] sync规则缺少trigger.element字段')
        return false
      }

      // 验证visibility类型特有的字段
      if (rule.type === 'visibility' && !rule.metadata.targetElement) {
        console.error('[代码页] visibility规则缺少targetElement字段')
        return false
      }

      return true
    },

    // 验证函数代码
    validateFunctionCode(funcCode) {
      /*
       * 匹配函数定义的基本结构
       * 允许: function xxx(...) { ... }
       * 忽略空白字符和换行
       * 允许函数前后有注释或其他内容
       */
      const functionRegex = /function\s+\w+\s*\([^)]*\)\s*\{[\s\S]*\}/i
      // 检查是否包含函数定义
      if (!functionRegex.test(funcCode)) {
        console.error('[代码页] 函数代码格式无效，必须包含: function xxx(...) { ... }')
        return false
      }
      return true
    },
    toggleEditMode() {
      console.log('[代码页] 切换编辑模式:', !this.isEditMode)

      if (this.isEditMode) {
        // 退出编辑模式
        if (this.codeExample === this.codeExampleBackup) {
          console.log('[代码页] 代码未更改，直接切换回查看模式')
          this.isEditMode = false
          // 仍然需要格式化
          this.codeExample = beautify.html(this.codeExample, {
            indent_size: 2,
            indent_char: ' ',
            max_preserve_newlines: 1,
            preserve_newlines: true,
            keep_array_indentation: false,
            break_chained_methods: false,
            indent_scripts: 'normal',
            brace_style: 'collapse',
            space_before_conditional: true,
            unescape_strings: false,
            jslint_happy: false,
            end_with_newline: true,
            wrap_line_length: 0,
            indent_inner_html: true,
            comma_first: false,
            e4x: false,
            indent_empty_lines: false,
            wrap_attributes: 'force',
            unformatted: ['pre', 'code'],
            content_unformatted: ['pre', 'code']
          })
          return
        }

        // 有更改，需要验证
        try {
          const { rules, isValid } = this.parseAndValidateCode(this.codeExample)
          if (!isValid) {
            // 验证失败，恢复备份
            this.codeExample = this.codeExampleBackup
            this.$message.error('代码格式错误，已恢复原值')
            this.isEditMode = false
            return
          }

          // 验证成功，发出更新
          console.log('[代码页] 代码验证通过，发出更新事件')
          this.$emit('update:formCode', this.codeExample)
        } catch (error) {
          // 处理解析错误
          this.codeExample = this.codeExampleBackup
          this.$message.error(`代码解析错误: ${error.message}`)
        }

        // 无论是否更改，最后都要格式化
        this.codeExample = beautify.html(this.codeExample, {
          indent_size: 2,
          indent_char: ' ',
          max_preserve_newlines: 1,
          preserve_newlines: true,
          keep_array_indentation: false,
          break_chained_methods: false,
          indent_scripts: 'normal',
          brace_style: 'collapse',
          space_before_conditional: true,
          unescape_strings: false,
          jslint_happy: false,
          end_with_newline: true,
          wrap_line_length: 0,
          indent_inner_html: true,
          comma_first: false,
          e4x: false,
          indent_empty_lines: false,
          wrap_attributes: 'force',
          unformatted: ['pre', 'code'],
          content_unformatted: ['pre', 'code']
        })

        this.isEditMode = false
      } else {
        // 进入编辑模式
        console.log('[代码页] 进入编辑模式，备份当前代码')
        this.codeExampleBackup = this.codeExample
        this.isEditMode = true
        // 确保 textarea 获取到正确的值
        this.$nextTick(() => {
          if (this.$refs.editor) {
            this.$refs.editor.value = this.codeExample
          }
        })
      }
    },
    copyCode() {
      console.log('[代码页] 正在复制代码到剪贴板')
      navigator.clipboard.writeText(this.codeExample).then(() => {
        console.log('[代码页] 代码复制成功')
        this.$message.success('代码已复制到剪贴板')
      }).catch((error) => {
        console.error('[代码页] 复制失败:', error)
        this.$message.error('复制失败')
      })
    },
    updateHighlight() {
      const code = this.codeExample
      this.highlightedCode = hljs.highlight(code, { language: this.language }).value
    },
    syncScroll(e) {
      const wrapper = this.$refs.editorWrapper
      if (wrapper) {
        requestAnimationFrame(() => {
          wrapper.scrollTop = e.target.scrollTop
          wrapper.scrollLeft = e.target.scrollLeft
        })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.editor-container {
  display: flex;
  flex-direction: column;
  background-color: #1e1e1e;
  border-radius: 8px;
  overflow: hidden;
  margin: 0 auto;
  height: 100vh; /* 使用视口高度 */
}

.editor-header {
  flex: none; /* 头部固定高度 */
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 16px;
  background-color: #252526;
  border-bottom: 1px solid #3c3c3c;

  .file-type {
    color: #fff;
    font-size: 14px;
  }

  .header-buttons {
    display: flex;
    gap: 8px;
  }
}

/* TODO: 代码显示模式切换问题
 * 问题：在只读/编辑模式切换时，滚动条行为不一致，调试多种方法无果
 * 临时解决方案：
 * - 只读模式：使用 white-space: pre-wrap 实现自动换行
 * - 编辑模式：使用 white-space: pre 保持水平滚动
 */
.editor-wrapper {
  flex: 1;
  position: relative;
  background-color: #1e1e1e;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

.code-display {
  padding: 16px;
  margin: 0;
  font-family: 'Consolas', 'Monaco', monospace;
  font-size: 14px;
  line-height: 1.5;
  white-space: pre-wrap;
  tab-size: 2;
  color: #d4d4d4;
  background: transparent;
  min-width: min-content;
  height: 500px;
  width: 100%;
  box-sizing: border-box;
  overflow: scroll;
  display: block;
}

.code-editor {
  padding: 16px;
  margin: 0;
  font-family: 'Consolas', 'Monaco', monospace;
  font-size: 14px;
  line-height: 1.5;
  white-space: pre-wrap;
  color: #d4d4d4;
  background: transparent;
  border: none;
  resize: none;
  outline: none;
  white-space: pre;
  tab-size: 2;
  min-width: min-content;
  height: 500px; /* TODO: 使用固定像素高度，此处遇到样式bug，暂时用固定值了 */
  width: 100%;
  box-sizing: border-box;
  overflow: auto;
}
</style>
